import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, ChevronUp, Save, WifiOff, CheckCircle2, X } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { OPS } from "@/constants/testIds";
import { clearDraft, draftKey, loadDraft, newCrid, saveDraft } from "@/lib/wmDraft";
import { attachPendingRecord, localKeyForRecord, localPhotoState } from "@/lib/coPhoto";
import { CoBranchSelect, ChipRow } from "./CoBranchSelect";
import { PhotoField, photoValueFor } from "./PhotoField";
import { DuplicateCard } from "./DuplicateCard";
import { PosLookupButton, PosVerdict } from "./PosVerdict";
import { CO_TIMEOUT_MS, coError, coMessage, composeOccurredAt, fmt, fmtSar, sanitizeValue, valueToHalalas } from "./coUtils";

const C = OPS.co;

const EMPTY = {
    invoice: "", value: "", outcome: "", phoneKind: "", phone: "", source: "branch", category: "",
    reason: "", outcomeNote: "", reissued: "", date: "", time: "",
};

// A photo taken in this form counts for «حفظ» only when the server has it or its bytes are
// still on their way from this tab (parked, or the in-memory PUT running) — incident 2026-10-21.
const PHOTO_ON_ITS_WAY = ["stored", "uploading", "pending"];
// what the form must say instead of «صورة الفاتورة مطلوبة»: retry or retake first
const PHOTO_BLOCKING = ["failed", "lost", "unchecked"];

/**
 * The till form (message 16 §7, 17 §3). Field order: camera → invoice
 * (autofocused after the photo) → value → 7 outcome chips → phone / «تيك اوي»
 * → source chips → category chips (branch top-5, rest behind «أسباب أخرى»;
 * «أخرى» expands the reason inline) → «المزيد» (date, time). Branch locked
 * for a cashier; a required picker + source locked to «الكول سنتر» for the
 * call-centre agent. `pos_lookup_mode: off` is rendered honestly: no «بحث»,
 * no POS note, no chips. Draft / idempotency / timeout rules = Waste & Meals.
 */
export function CashierForm({ scope, onSaved, editing, onCancelEdit }) {
    const { t, lang } = useI18n();
    const { me } = useAuth();
    const userId = me?.user?.id;
    const isAgent = scope?.source_locked === "call_center";
    const settings = scope?.settings || {};
    const labels = scope?.labels || {};

    const [branchId, setBranchId] = useState(isAgent ? "" : (scope?.default_branch_id || ""));
    const [f, setF] = useState(EMPTY);
    const [photo, setPhoto] = useState({ key: null, state: "none", preview: null });
    const [valueConfirmed, setValueConfirmed] = useState(false);
    const [moreCats, setMoreCats] = useState(false);
    const [more, setMore] = useState(false);
    const [busy, setBusy] = useState(false);
    const [failed, setFailed] = useState(false);
    const [dup, setDup] = useState(null);
    const [confirmation, setConfirmation] = useState(null);
    const [pos, setPos] = useState(null);
    const crid = useRef(newCrid());
    const invoiceRef = useRef(null);
    // message 35: a live lookup may take 15 s — only the latest one may set the chip
    const lookupSeq = useRef(0);

    // POS lookup (CP4): only when the branch's effective mode is not `off`; the
    // agent gets no button while `pos_match_call_center` is `off` (the endpoint is 404).
    const posMode = (scope?.pos_lookup_effective || {})[branchId] || "off";
    const lookupOn = posMode !== "off" && !(isAgent && (settings.pos_match_call_center || "off") === "off");
    const minDigits = settings.pos_min_suffix_digits || 4;
    const digitsTyped = (f.invoice.match(/\d/g) || []).length;

    const lookup = async () => {
        if (!lookupOn || !branchId || digitsTyped < minDigits) return;
        const seq = ++lookupSeq.current;
        setPos({ match: "checking", live: posMode === "live_background" });
        try {
            const params = { branch_id: branchId, invoice_no: f.invoice.trim() };
            const occurred = composeOccurredAt(f.date, f.time);
            if (occurred) params.occurred_at = occurred;
            // message 35: a live branch may ask the POS (≤ 15 s server budget) — the client waits longer
            const r = await api.get("/co/pos-lookup", { params, timeout: CO_TIMEOUT_MS + 10000 });
            if (seq === lookupSeq.current) setPos(r.data);
        } catch (e) {
            if (seq !== lookupSeq.current) return;
            const err = coError(e);
            if (err?.code === "CO_LOOKUP_RATE_LIMIT" || err?.code === "CO_LOOKUP_DAILY_CAP") setPos({ match: "error", message: t("co_pos_lookup_limit") });
            // a client timeout, a dropped connection or a 5xx: the record's own checks still run
            // after the save (17 §2.4), so say «unavailable» rather than leave nothing
            else if (!e?.response || e.response.status >= 500) setPos({ match: "unavailable" });
            else setPos(null);
        }
    };
    // A late answer must never land under another invoice, branch or form.
    const dropLookup = () => { lookupSeq.current += 1; setPos(null); };

    const dkey = draftKey({ userId, branchId, date: scope?.business_date, type: "co", kind: "co" });

    // ---- draft (per user + branch + business day), photo key included
    useEffect(() => {
        if (editing) return undefined;
        // another branch / day, or back from an edit: a lookup in flight belongs to the old form
        lookupSeq.current += 1; setPos(null);
        let alive = true;
        const d = loadDraft(dkey);
        if (d?.f) {
            setF({ ...EMPTY, ...d.f });
            if (d.photoKey) {
                // parked here → retried; otherwise the server says (stored, or `lost` → retake) — never assumed
                const key = d.photoKey;
                setPhoto({ key, state: "checking", preview: null });
                photoValueFor(key).then((v) => {
                    if (alive) setPhoto((cur) => (cur.key === key && cur.state === "checking" ? v : cur));
                });
            }
        }
        return () => { alive = false; };
    }, [dkey, editing]);

    useEffect(() => {
        if (editing) return;
        const dirty = Object.keys(EMPTY).some((k) => k !== "source" && f[k]);
        if (dirty || photo.key) saveDraft(dkey, { f, photoKey: photo.key });
    }, [dkey, f, photo.key, editing]);

    // ---- edit mode: load the record into the same form
    useEffect(() => {
        if (!editing) return undefined;
        let alive = true;
        setBranchId(editing.branch_id);
        const occ = editing.occurred_at ? new Date(editing.occurred_at) : null;
        const riyadh = occ ? new Date(occ.getTime() + 3 * 3600 * 1000) : null;
        setF({
            invoice: editing.invoice_no || "", value: editing.value_sar != null ? String(editing.value_sar) : "",
            outcome: editing.outcome || "", phoneKind: editing.phone_kind || "", phone: "",
            source: editing.source || "branch", category: editing.category || "", reason: editing.reason_text || "",
            outcomeNote: editing.outcome_note || "", reissued: editing.reissued_invoice_no || "",
            date: riyadh ? riyadh.toISOString().slice(0, 10) : "", time: riyadh ? riyadh.toISOString().slice(11, 16) : "",
        });
        // the photo as the server has it: stored → its thumbnail; pending / failed → still on its way
        // from this device, or `lost` (retake) — never «تم حفظ الصورة» without the bytes
        const st = editing.photo_status || "missing";
        if (st === "stored") {
            setPhoto({ key: null, state: "stored", preview: null });
            if (editing.has_photo) {
                api.get(`/co/records/${editing.id}/photo`, { params: { json: 1 }, timeout: CO_TIMEOUT_MS })
                    .then((r) => {
                        if (alive) setPhoto((cur) => (!cur.key && cur.state === "stored" ? { ...cur, preview: r.data.url } : cur));
                    })
                    .catch(() => { /* the status line still says stored; no thumbnail */ });
            }
        } else if (st === "pending" || st === "failed") {
            setPhoto({ key: null, state: "checking", preview: null });
            localKeyForRecord(editing.id)
                .then((key) => (key ? photoValueFor(key) : { key: null, state: "lost", preview: null }))
                .then((v) => { if (alive) setPhoto((cur) => (!cur.key && cur.state === "checking" ? v : cur)); });
        } else {
            setPhoto({ key: null, state: "missing", preview: null });
        }
        setValueConfirmed(true);
        setDup(null); setFailed(false); setConfirmation(null);
        lookupSeq.current += 1; setPos(null);
        crid.current = newCrid();
        return () => { alive = false; };
    }, [editing]);

    const set = (k) => (v) => { setFailed(false); setDup(null); crid.current = newCrid(); if (k === "invoice") dropLookup(); setF((s) => ({ ...s, [k]: v })); };
    const setValue = (v) => { setValueConfirmed(false); set("value")(sanitizeValue(v)); };

    const halalas = valueToHalalas(f.value);
    const warn = halalas != null && settings.warn_value_halalas && halalas >= settings.warn_value_halalas;
    const phoneKind = isAgent ? "mobile" : f.phoneKind;
    const source = isAgent ? "call_center" : f.source;

    const topCats = useMemo(() => {
        const all = labels.categories || [];
        const top = (scope?.category_top_by_branch || {})[branchId] || all.slice(0, 5).map((c) => c.key);
        const first = top.map((k) => all.find((c) => c.key === k)).filter(Boolean);
        const rest = all.filter((c) => !top.includes(c.key));
        return { first, rest };
    }, [labels.categories, scope, branchId]);
    const catVisible = moreCats || topCats.rest.some((c) => c.key === f.category)
        ? [...topCats.first, ...topCats.rest] : topCats.first;

    // A record being edited keeps the photo it was saved with, whatever became of it: the PATCH then
    // carries no photo key and the server leaves the record's photo as it is (message 29 §1.4 — the
    // edit of a record whose photo never arrived saves, photo_status unchanged). The field still says
    // lost / failed and offers the retake. A photo taken in THIS form (`fresh`, create or edit) must
    // be stored or on its way; failed / lost / unchecked: retry or retake first.
    const ownPhoto = !!editing && !photo.fresh;
    const photoOk = !settings.photo_required || ownPhoto
        || (!!photo.key && PHOTO_ON_ITS_WAY.includes(photo.state));
    const photoBlocked = !!settings.photo_required && !ownPhoto && PHOTO_BLOCKING.includes(photo.state);
    const canSave = !!branchId && photoOk && f.invoice.trim() && halalas != null && f.outcome && phoneKind
        && (phoneKind !== "mobile" || f.phone.trim() || (editing && editing.phone_masked && !f.phone))
        && f.category && (!warn || valueConfirmed) && !busy;

    const payload = () => {
        const body = {
            branch_id: branchId, invoice_no: f.invoice.trim(), value: f.value, outcome: f.outcome,
            phone_kind: phoneKind, source, category: f.category,
            reason_text: f.category === "other" ? f.reason : null,
            outcome_note: f.outcome === "other" ? f.outcomeNote : null,
            reissued_invoice_no: f.outcome === "reissued" ? (f.reissued || null) : null,
            // an edit sends a key only for a photo taken in this form; the record's own stays as it is
            photo_key: (ownPhoto ? null : photo.key) || null,
        };
        if (phoneKind === "mobile" && f.phone.trim()) body.customer_phone = f.phone.trim();
        const occurred = composeOccurredAt(f.date, f.time);
        if (occurred) body.occurred_at = occurred;
        return body;
    };

    const resetForNext = () => {
        setF({ ...EMPTY, source: isAgent ? "branch" : "branch" });
        setPhoto({ key: null, state: "none", preview: null });
        setValueConfirmed(false); setMoreCats(false); setMore(false); setDup(null); setFailed(false); dropLookup();
        crid.current = newCrid();
        clearDraft(dkey);
    };

    // After the save: the server's `photo_status`. Not stored and nothing left in this tab to send
    // it (no PUT running, nothing parked, no 2xx seen here) → the record stands without its photo.
    const warnPhoto = async (rec, key) => {
        const st = rec?.photo_status;
        if (!st || st === "stored" || st === "missing") return;
        const local = key ? await localPhotoState(key) : null;
        if (local) return;
        toast.warning(t("co_photo_saved_without"), { id: `co-photo-saved-${rec.id}` });
    };

    const save = async (confirmDuplicate = false) => {
        if ((!canSave && !confirmDuplicate) || !photoOk) return;
        setBusy(true);
        try {
            if (editing) {
                // the key on this device (the record's own, or a retake): the warning asks whether it is
                // still on its way; only a retake was sent in the PATCH
                const photoKey = photo.key;
                const r = await api.patch(`/co/records/${editing.id}`, { ...payload(), confirm_duplicate: confirmDuplicate },
                    { timeout: CO_TIMEOUT_MS });
                toast.success(t("co_updated"));
                setDup(null);
                if (photoKey) await attachPendingRecord(photoKey, r.data.id);
                warnPhoto(r.data, photoKey);
                onSaved && (await onSaved(r.data));
                onCancelEdit && onCancelEdit();
                return;
            }
            const r = await api.post("/co/records", { ...payload(), client_request_id: crid.current, confirm_duplicate: confirmDuplicate },
                { timeout: CO_TIMEOUT_MS });
            const rec = r.data.record;
            const photoKey = photo.key;
            setConfirmation({ invoice: rec.invoice_no, value: rec.value_sar, duplicate: !!rec.duplicate_of });
            toast.success(t("co_saved"));
            resetForNext();
            if (photoKey) await attachPendingRecord(photoKey, rec.id);
            warnPhoto(rec, photoKey);
            onSaved && (await onSaved(rec));
        } catch (e) {
            const err = coError(e);
            if (err?.code === "CO_DUPLICATE_INVOICE") { setDup(err); return; }
            const msg = coMessage(e, lang);
            if (msg) toast.error(msg); else setFailed(true);
        } finally {
            setBusy(false);
        }
    };

    const chip = (list, key) => ({ key, name_ar: list.find((x) => x.key === key)?.name_ar, name_en: list.find((x) => x.key === key)?.name_en });
    const phoneKinds = (labels.phone_kinds || []).filter((k) => k !== "unknown").map((k) => ({
        key: k, name_ar: t(`co_phone_kind_${k}`), name_en: t(`co_phone_kind_${k}`) }));

    return (
        <div data-testid={C.form} data-mode={editing ? "edit" : "create"}
             className="space-y-3 rounded-[var(--radius-lg)] border border-[color:var(--border-soft)] bg-[color:var(--bg-surface)] p-3 shadow-[var(--shadow-sm)]">
            {editing ? (
                <div className="flex items-center justify-between rounded-[var(--radius-sm)] bg-[color:var(--brand-orange-soft)] px-3 py-2 text-xs font-bold text-[color:var(--brand-brown)]">
                    <span>{t("co_editing")} · {editing.invoice_no}</span>
                    <button type="button" data-testid={C.cancelEdit} onClick={onCancelEdit} className="inline-flex items-center gap-1">
                        <X className="h-3.5 w-3.5" />{t("co_cancel_edit")}
                    </button>
                </div>
            ) : null}

            {confirmation && !editing ? (
                <div data-testid={C.confirmation}
                     className="flex items-start gap-2 rounded-[var(--radius-sm)] border border-[color:var(--success,#15803d)]/30 bg-[color:var(--success-soft,rgba(21,128,61,0.08))] px-3 py-2 text-xs font-semibold text-[color:var(--success,#15803d)]">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
                    <div>
                        <div>{fmt(t("co_confirmation"), { invoice: confirmation.invoice, value: fmtSar(confirmation.value, lang, { halalas: false }) })}</div>
                        {confirmation.duplicate ? <div className="text-[11px] opacity-80">{t("co_confirmation_duplicate")}</div> : null}
                    </div>
                </div>
            ) : null}

            {/* branch — locked for a cashier, required picker for the agent */}
            <CoBranchSelect branches={scope?.branches} value={branchId} required={isAgent}
                            onChange={(v) => { setBranchId(v); setDup(null); crid.current = newCrid(); dropLookup(); }} />
            {isAgent && !branchId ? (
                <p className="text-[11px] font-semibold text-[color:var(--ink-muted)]">{t("co_branch_required")}</p>
            ) : null}

            {/* 1. camera */}
            {/* a NEW photo (an object) is an edit of the payload, taken in this form (`fresh`); the same
                photo moving on (an updater: uploading → stored, a retry) changes nothing the server sees,
                so the request id stays */}
            <PhotoField branchId={branchId} value={photo} disabled={busy}
                        onChange={(v) => {
                            if (typeof v === "function") { setPhoto(v); return; }
                            setFailed(false); setDup(null); crid.current = newCrid(); setPhoto({ ...v, fresh: true });
                        }}
                        onCaptured={() => invoiceRef.current?.focus()} />

            {/* 2. invoice — numeric keypad hint, LTR; «بحث» only when the branch's lookup mode is not `off` */}
            <div className="space-y-1">
                <div className="flex items-stretch gap-2">
                    <Input ref={invoiceRef} data-testid={C.invoice} value={f.invoice} onChange={(e) => set("invoice")(e.target.value)}
                           inputMode="numeric" dir="ltr" placeholder={t("co_invoice")} autoComplete="off"
                           onKeyDown={(e) => {
                               if (e.key !== "Enter" || !lookupOn) return;
                               e.preventDefault();
                               // like the disabled «بحث»: no second lookup while one is checking
                               if (pos?.match === "checking") return;
                               lookup();
                           }}
                           className="h-11 min-w-0 flex-1 text-start text-base font-semibold" />
                    {lookupOn ? (
                        <PosLookupButton testId={C.posLookup} t={t} busy={pos?.match === "checking"} live={posMode === "live_background"}
                                         disabled={!branchId || digitsTyped < minDigits || busy} onClick={lookup} />
                    ) : null}
                </div>
                <p data-testid={C.invoiceHelper} className="text-[11px] text-[color:var(--ink-faint)]">{t("co_invoice_helper")}</p>
                {lookupOn && pos ? <PosVerdict v={pos} mode={pos.mode || posMode} minDigits={minDigits} t={t} lang={lang} testId={C.posResult} /> : null}
                {dup ? <DuplicateCard detail={dup} busy={busy} onConfirm={() => save(true)} onCancel={() => setDup(null)} /> : null}
            </div>

            {/* 3. value */}
            <div className="space-y-1">
                <div className="relative">
                    <Input data-testid={C.value} value={f.value} onChange={(e) => setValue(e.target.value)}
                           inputMode="decimal" dir="ltr" placeholder={t("co_value")}
                           className="h-11 pe-14 text-start text-base font-semibold" />
                    <span className="pointer-events-none absolute inset-y-0 end-3 flex items-center text-xs font-semibold text-[color:var(--ink-faint)]">
                        {lang === "ar" ? "ر.س" : "SAR"}
                    </span>
                </div>
                {warn && !valueConfirmed ? (
                    <div data-testid={C.valueWarn} className="flex items-center justify-between gap-2 rounded-[var(--radius-sm)] bg-[color:var(--warning-soft)] px-3 py-2">
                        <span className="text-xs font-semibold text-[color:var(--warning-soft-fg)]">{t("co_value_warn")}</span>
                        <Button size="sm" variant="outline" data-testid={C.valueConfirm} onClick={() => setValueConfirmed(true)} className="h-8 text-xs">
                            {t("co_value_confirm")}
                        </Button>
                    </div>
                ) : null}
            </div>

            {/* 4. outcome — one row of seven chips */}
            <div className="space-y-1">
                <p className="text-xs font-bold text-[color:var(--ink-muted)]">{t("co_outcome")}</p>
                <ChipRow items={labels.outcomes || []} value={f.outcome} onChange={set("outcome")} testId={C.outcome} lang={lang} disabled={busy} />
                {f.outcome === "other" ? (
                    <Input data-testid={C.outcomeNote} value={f.outcomeNote} onChange={(e) => set("outcomeNote")(e.target.value)}
                           placeholder={t("co_outcome_note")} className="h-10" />
                ) : null}
                {f.outcome === "reissued" ? (
                    <Input data-testid={C.reissued} value={f.reissued} onChange={(e) => set("reissued")(e.target.value)}
                           inputMode="numeric" dir="ltr" placeholder={t("co_reissued_invoice")} className="h-10 text-start" />
                ) : null}
            </div>

            {/* 5. customer — «العميل»: phone / «تيك اوي» / «تطبيق توصيل» */}
            <div className="space-y-1">
                {!isAgent ? (
                    <>
                        <p data-testid={C.customerHeading} className="text-xs font-bold text-[color:var(--ink-muted)]">{t("co_customer")}</p>
                        <ChipRow items={phoneKinds} value={f.phoneKind} onChange={set("phoneKind")} testId={C.phoneKind} lang={lang} disabled={busy} />
                    </>
                ) : (
                    <p data-testid={C.customerHeading} className="text-xs font-bold text-[color:var(--ink-muted)]">{t("co_customer")}</p>
                )}
                {phoneKind === "mobile" ? (
                    <Input data-testid={C.phone} value={f.phone} onChange={(e) => set("phone")(e.target.value)}
                           type="tel" inputMode="tel" dir="ltr" autoComplete="off"
                           placeholder={editing?.phone_masked && !f.phone ? editing.phone_masked : t("co_phone")}
                           className="h-11 text-start text-base font-semibold" />
                ) : null}
            </div>

            {/* 6. source — default «الفرع»; locked for the agent */}
            <div className="space-y-1">
                <p className="text-xs font-bold text-[color:var(--ink-muted)]">{t("co_source")}</p>
                {isAgent ? (
                    <div data-testid={C.sourceLocked}
                         className="inline-flex h-9 items-center gap-2 rounded-full border border-[color:var(--border-soft)] bg-[color:var(--bg-surface-2)] px-3 text-xs font-semibold text-[color:var(--ink)]">
                        {lang === "ar" ? chip(labels.sources || [], "call_center").name_ar : chip(labels.sources || [], "call_center").name_en}
                        <span className="text-[10px] text-[color:var(--ink-faint)]">{t("co_source_locked_hint")}</span>
                    </div>
                ) : (
                    <ChipRow items={labels.sources || []} value={f.source} onChange={set("source")} testId={C.source} lang={lang} disabled={busy} />
                )}
            </div>

            {/* 7. category — branch top-5 first, the rest behind «أسباب أخرى»; «أخرى» expands the reason inline */}
            <div className="space-y-1">
                <p className="text-xs font-bold text-[color:var(--ink-muted)]">{t("co_category")}</p>
                <ChipRow items={catVisible} value={f.category} onChange={set("category")} testId={C.category} lang={lang} disabled={busy} />
                {topCats.rest.length ? (
                    <button type="button" data-testid={C.categoryMore} onClick={() => setMoreCats((v) => !v)}
                            className="inline-flex items-center gap-1 text-xs font-semibold text-[color:var(--brand-brown)]">
                        {moreCats ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                        <span>{moreCats ? t("co_category_less") : t("co_category_more")}</span>
                    </button>
                ) : null}
                {f.category === "other" ? (
                    <Textarea data-testid={C.reason} rows={2} value={f.reason} onChange={(e) => set("reason")(e.target.value)}
                              placeholder={t("co_reason")} />
                ) : null}
            </div>

            {/* 8. «المزيد» — date + time (defaults: business day, now) */}
            <button type="button" data-testid={C.moreToggle} onClick={() => setMore((v) => !v)}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-[color:var(--brand-brown)]">
                {/* the chevron changes with the toggle: the label keeps its own element (a translated page — PhotoField) */}
                {more ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                <span>{more ? t("co_less") : t("co_more")}</span>
            </button>
            {more ? (
                <div className="grid grid-cols-2 gap-2">
                    <Input data-testid={C.date} type="date" dir="ltr" value={f.date || scope?.business_date || ""}
                           max={scope?.business_date} onChange={(e) => set("date")(e.target.value)} className="h-10" />
                    <Input data-testid={C.time} type="time" dir="ltr" value={f.time}
                           onChange={(e) => { set("time")(e.target.value); if (!f.date) set("date")(scope?.business_date || ""); }} className="h-10" />
                </div>
            ) : null}

            {failed ? (
                <div data-testid={C.offlineError}
                     className="flex items-center justify-between gap-2 rounded-[var(--radius-sm)] bg-[color:var(--danger-soft,rgba(220,38,38,0.08))] px-3 py-2">
                    <span className="flex items-center gap-1.5 text-xs font-semibold text-[color:var(--danger)]">
                        <WifiOff className="h-3.5 w-3.5" />{t("co_offline_error")}
                    </span>
                    <Button size="sm" variant="outline" data-testid={C.retry} onClick={() => save(false)} disabled={busy}>
                        {t("co_retry")}
                    </Button>
                </div>
            ) : null}

            {photoBlocked ? (
                <p data-testid={C.photoBlocked} role="alert" className="text-[11px] font-semibold text-[color:var(--danger)]">
                    {t("co_photo_save_blocked")}
                </p>
            ) : !photoOk && f.invoice && photo.state !== "checking" ? (
                <p className="text-[11px] font-semibold text-[color:var(--ink-muted)]">{t("co_photo_required")}</p>
            ) : null}

            <Button data-testid={C.save} onClick={() => save(false)} disabled={!canSave}
                    className="h-12 w-full gap-2 text-sm font-bold">
                <Save className="h-4 w-4" />
                {busy ? t("co_saving") : (editing ? t("co_update") : t("co_save"))}
            </Button>
        </div>
    );
}
